import { useAuth } from '../context/AuthContext';
import { useEnvironment, ENV_COLORS } from '../context/EnvironmentContext';
import {
  Shield,
  Database,
  Activity,
  Brain,
  Store,
  Users,
  Newspaper,
  BarChart3,
} from 'lucide-react';

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

function getDisplayName(
  user: { displayName?: string; firstName?: string; email?: string; username?: string } | null,
): string {
  if (!user) return '';
  if (user.firstName) return user.firstName;
  if (user.displayName) return user.displayName.split(' ')[0];
  const source = user.email?.split('@')[0] ?? user.username ?? '';
  if (!source) return '';
  const cleaned = source.replace(/^Yum_/i, '').split(/[._-]/)[0];
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

export function HomePage() {
  const { user, can } = useAuth();
  const { environment } = useEnvironment();
  const envColors = ENV_COLORS[environment];

  const quickActions = [
    {
      label: 'Restaurants',
      icon: Store,
      path: '/stores',
      domain: 'stores',
      description: 'Search restaurants, view details, browse by area',
    },
    {
      label: 'Users',
      icon: Users,
      path: '/users',
      domain: 'users',
      description: 'Search users, manage profiles and hierarchy',
    },
    {
      label: 'Content',
      icon: Newspaper,
      path: '/feeds',
      domain: 'feeds',
      description: 'Search feeds and analyze engagement',
    },
    {
      label: 'Equipment',
      icon: BarChart3,
      path: '/stores/equipment',
      domain: 'stores',
      description: 'Equipment and routine analysis by brand',
    },
  ].filter((action) => can(environment, action.domain, 'read'));

  return (
    <div className="animate-fade-in space-y-8">
      {/* Greeting */}
      <div>
        <h1 className="font-display text-3xl font-bold text-ink">
          {getGreeting()}
          {getDisplayName(user) ? `, ${getDisplayName(user)}` : ''}
        </h1>
        <p className="mt-1 text-ink-secondary">Welcome to Byte Coach CMDB</p>
      </div>

      {/* Status bar */}
      <div className="flex flex-wrap gap-3">
        <div className={`badge ${envColors.bg} ${envColors.text}`}>
          <span className={`mr-1.5 h-1.5 w-1.5 rounded-full ${envColors.dot} inline-block`} />
          {envColors.label} environment
        </div>
        {user?.roles?.[0] && (
          <div className="badge bg-accent-subtle text-accent">
            <Shield className="mr-1 h-3 w-3" />
            {user.roles[0]}
          </div>
        )}
      </div>

      {/* Quick actions */}
      {quickActions.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-ink-secondary uppercase tracking-wider mb-3">
            Quick actions
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {quickActions.map((action) => (
              <a
                key={action.path}
                href={action.path}
                className="card p-5 hover:shadow-card-hover transition-shadow group"
              >
                <action.icon className="h-5 w-5 text-accent mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-medium text-ink">{action.label}</h3>
                <p className="mt-1 text-sm text-ink-secondary">{action.description}</p>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Platform capabilities */}
      <div>
        <h2 className="text-sm font-semibold text-ink-secondary uppercase tracking-wider mb-3">
          Platform capabilities
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="card p-5">
            <Shield className="h-5 w-5 text-brand-purple mb-3" />
            <h3 className="font-medium text-ink">RBAC</h3>
            <p className="mt-1 text-sm text-ink-secondary">
              {user?.capabilities?.length ?? 0} capabilities resolved
            </p>
          </div>
          <div className="card p-5">
            <Database className="h-5 w-5 text-brand-orange mb-3" />
            <h3 className="font-medium text-ink">Multi-Environment</h3>
            <p className="mt-1 text-sm text-ink-secondary">Dev, Staging, Production databases</p>
          </div>
          <div className="card p-5">
            <Activity className="h-5 w-5 text-brand-crimson mb-3" />
            <h3 className="font-medium text-ink">Audit Logging</h3>
            <p className="mt-1 text-sm text-ink-secondary">
              Every action tracked with before/after
            </p>
          </div>
          <div className="card p-5">
            <Brain className="h-5 w-5 text-accent mb-3" />
            <h3 className="font-medium text-ink">AI Powered</h3>
            <p className="mt-1 text-sm text-ink-secondary">
              Claude via Bedrock for summaries and NL2SQL
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
